import {
  LEGACY_RISK_MATRIX,
  RA_LIBRARY_STATUSES,
  type RaAcceptor,
  type RaLibraryContent,
  type RaLibraryStatus,
} from '@felix/domain';
import { Button, Modal, Select, TextInput } from '@mantine/core';
import {
  IconArrowLeft,
  IconBooks,
  IconCircleCheck,
  IconClipboardCheck,
  IconEdit,
  IconFilePlus,
  IconPlus,
  IconSearch,
  IconSend,
  IconShieldCheck,
  IconUpload,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { formatDay } from '../../../format.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import { StatusBadge } from '../../../StatusBadge.js';
import table from '../../materials/Materials.module.css';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LoadError, NoMatch, TablePager, TableSkeleton } from '../../purchasing/table.js';
import { ReturnedBanner } from '../../returned/Returned.js';
import master from '../master/RaMaster.module.css';
import { ActionDialog, LevelBadge } from '../RiskParts.js';
import { AcceptanceBadge, BUILDER_TABS, type BuilderTab, RaBuilder } from './RaBuilder.js';
import classes from './RaLibrary.module.css';
import { RaPublication, RaStatusBadge, VersionList } from './RaPublication.js';
import type {
  RaEventKind,
  RaLibraryDetail,
  RaLibraryList,
  RaLibrarySource,
  RaVersionView,
  RaVesselLibrary,
} from './types.js';
import {
  assignmentLabel,
  EMPTY_LIBRARY_FILTER,
  filterLibrary,
  filterVesselLibrary,
  type LibraryFilter,
  libraryStats,
} from './view.js';

const PAGE_SIZE = 15;

type Notice = { tone: 'success' | 'danger'; text: string };

export interface RaLibraryScreenProps {
  source: RaLibrarySource;
}

/** Hata kodunun metni: kütüphane metinleri, yoksa risk metinleri. */
function useLibraryError() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = typeof error === 'string' ? error : errorCode(error);
    const detail = (error as { detail?: unknown } | null)?.detail;
    return (
      t(`riskLibrary.errors.${code}`, {
        detail: typeof detail === 'string' ? detail : '',
        defaultValue: '',
      }) ||
      t(`riskLibrary.issues.${code}`, { n: '', defaultValue: '' }) ||
      t(`risk.errors.${code}`, { defaultValue: '' }) ||
      `${t('riskLibrary.errors.GENERIC')} (${code})`
    );
  };
}

/**
 * Compliance ▸ Risk Assessment ▸ Library (K3; ADR 0087). Ofiste şirket RA'larının listesi ve
 * yedi sekmeli Builder: taslak, gönderim, inceleme, iade, DPA kabulü, onay, yayım, yeni revizyon
 * ve gemi ataması. Gemide yalnız yayımlı sürümler, atanmışsa "güncel"; salt okunur. Tasarım yok;
 * tablo dili Master Data.
 */
export function RaLibraryScreen({ source }: RaLibraryScreenProps) {
  return source.office ? (
    <OfficeLibrary source={source} office={source.office} />
  ) : (
    <ShipLibrary source={source} />
  );
}

// ---------------------------------------------------------------------------------------------
// Ofis

function OfficeLibrary({
  source,
  office,
}: {
  source: RaLibrarySource;
  office: NonNullable<RaLibrarySource['office']>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.${key}`, options);
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; list: RaLibraryList }
  >({ kind: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [creating, setCreating] = useState(false);
  const message = useLibraryError();
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', list: await office.list() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [office]);
  useEffect(() => {
    void load();
  }, [load]);
  const list = state.kind === 'ready' ? state.list : null;
  const unassessed = list?.activities.filter((a) => !a.assessed) ?? [];
  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconBooks size={26} />}
        actions={
          list && !selected && list.can.create ? (
            <>
              <Button
                variant="default"
                leftSection={<IconFilePlus size={16} />}
                disabled={unassessed.length === 0}
                onClick={async () => {
                  try {
                    const result = await office.drafts();
                    setNotice({
                      tone: 'success',
                      text: k('actions.draftsDone', {
                        count: result.created.length,
                        skipped: result.skipped.length,
                      }),
                    });
                    await load();
                  } catch (error) {
                    setNotice({ tone: 'danger', text: message(error) });
                  }
                }}
              >
                {k('actions.drafts')}
              </Button>
              <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
                {k('actions.new')}
              </Button>
            </>
          ) : undefined
        }
      />
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('empty.error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !list ? (
        <Panel flush aria-label={k('title')}>
          <TableSkeleton columns={8} label={k('title')} />
        </Panel>
      ) : selected ? (
        <OfficeDetail
          id={selected}
          list={list}
          office={office}
          source={source}
          onBack={() => {
            setSelected(null);
            void load();
          }}
          onNotice={setNotice}
        />
      ) : (
        <OfficeList
          list={list}
          onOpen={(id) => {
            setNotice(null);
            setSelected(id);
          }}
        />
      )}
      <Modal
        opened={creating}
        onClose={() => setCreating(false)}
        title={k('newDialog.title')}
        closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
      >
        <NewAssessment
          activities={unassessed}
          onCreate={async (activityId) => {
            try {
              const created = await office.create(activityId);
              setCreating(false);
              setNotice({
                tone: 'success',
                text: k('actions.done.created', { code: created.code }),
              });
              setSelected(created.id);
              return null;
            } catch (error) {
              return message(error);
            }
          }}
        />
      </Modal>
    </div>
  );
}

function NewAssessment({
  activities,
  onCreate,
}: {
  activities: readonly { id: string; code: string; name: string }[];
  onCreate: (activityId: string) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`riskLibrary.newDialog.${key}`);
  const [activity, setActivity] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className={classes.body}
      style={{ padding: 0 }}
      onSubmit={async (event) => {
        event.preventDefault();
        if (!activity) return;
        setBusy(true);
        setError(await onCreate(activity));
        setBusy(false);
      }}
    >
      <Select
        label={k('activity')}
        description={k('help')}
        placeholder={activities.length > 0 ? k('placeholder') : k('none')}
        disabled={activities.length === 0}
        searchable
        value={activity}
        data={activities.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))}
        onChange={setActivity}
      />
      {error ? (
        <p className={classes.issues} role="alert">
          {error}
        </p>
      ) : null}
      <div className={classes.actions}>
        <Button type="submit" disabled={!activity} loading={busy}>
          {k('create')}
        </Button>
      </div>
    </form>
  );
}

export function OfficeList({
  list,
  onOpen,
}: {
  list: RaLibraryList;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.${key}`, options);
  const [filter, setFilter] = useState<LibraryFilter>(EMPTY_LIBRARY_FILTER);
  const [page, setPage] = useState(1);
  const items = useMemo(() => filterLibrary(list.items, filter), [list.items, filter]);
  const stats = useMemo(() => libraryStats(list.items), [list.items]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const groupName = (g: string) => t(`riskMaster.groups.${g}`);
  return (
    <>
      <div className={master.stats}>
        <StatTile
          icon={<IconShieldCheck size={22} />}
          label={k('stats.published')}
          value={stats.published}
          variant="plain"
        />
        <StatTile
          icon={<IconClipboardCheck size={22} />}
          label={k('stats.workflow')}
          value={stats.workflow}
          tone={stats.review > 0 ? 'warning' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconEdit size={22} />}
          label={k('stats.drafts')}
          value={stats.drafts}
          variant="plain"
        />
        <StatTile
          icon={<IconUpload size={22} />}
          label={k('stats.assigned')}
          value={stats.assigned}
          variant="plain"
        />
      </div>
      <p className={master.hint}>
        {k('stats.publishedNote', { count: stats.migrated })} ·{' '}
        {k('stats.workflowNote', { review: stats.review })} ·{' '}
        {k('stats.draftsNote', { missing: stats.missing })} ·{' '}
        {k('stats.assignedNote', { count: stats.published })}
      </p>
      <Panel flush aria-label={k('title')}>
        <div className={master.toolbar}>
          <TextInput
            className={master.search}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={filter.query}
            onChange={(event) => {
              setFilter({ ...filter, query: event.currentTarget.value });
              setPage(1);
            }}
          />
          <Select
            className={master.filter}
            aria-label={k('filter.status')}
            allowDeselect={false}
            value={filter.status}
            data={[
              { value: 'all', label: k('filter.allStatuses') },
              ...RA_LIBRARY_STATUSES.map((s) => ({ value: s, label: k(`statuses.${s}`) })),
            ]}
            onChange={(value) => {
              setFilter({ ...filter, status: (value ?? 'all') as RaLibraryStatus | 'all' });
              setPage(1);
            }}
          />
        </div>
        {list.items.length === 0 ? (
          <EmptyState title={k('empty.title')} description={k('empty.office')} />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('empty.noMatch')}
            onClear={() => {
              setFilter(EMPTY_LIBRARY_FILTER);
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('tableLabel')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${purchasing.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{k('columns.code')}</th>
                    <th scope="col">{k('columns.title')}</th>
                    <th scope="col">{k('columns.revision')}</th>
                    <th scope="col">{k('columns.status')}</th>
                    <th scope="col" data-numeric>
                      {k('columns.rows')}
                    </th>
                    <th scope="col">{k('columns.inherent')}</th>
                    <th scope="col">{k('columns.residual')}</th>
                    <th scope="col">{k('columns.acceptance')}</th>
                    <th scope="col">{k('columns.assigned')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <button
                          type="button"
                          className={master.codeButton}
                          aria-label={k('open', { code: item.code })}
                          onClick={() => onOpen(item.id)}
                        >
                          {item.code}
                        </button>
                        {item.migrated ? <span className={master.sub}>{k('migrated')}</span> : null}
                      </td>
                      <td className={master.wrapCell}>{item.title}</td>
                      <td>
                        {item.revision}
                        <span className={master.sub}>
                          {item.publishedRevision
                            ? t('riskLibrary.header.published', {
                                revision: item.publishedRevision,
                              })
                            : t('riskLibrary.header.noPublished')}
                        </span>
                      </td>
                      <td>
                        <RaStatusBadge status={item.status} />
                      </td>
                      <td data-numeric>{item.rows}</td>
                      <td>
                        {item.inherent ? (
                          <LevelBadge level={item.inherent.level} score={item.inherent.score} />
                        ) : (
                          k('none')
                        )}
                      </td>
                      <td>
                        {item.residual ? (
                          <LevelBadge level={item.residual.level} score={item.residual.score} />
                        ) : (
                          k('none')
                        )}
                        {item.missingResidual > 0 ? (
                          <span className={master.sub}>
                            {k('missingResidual', { count: item.missingResidual })}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <AcceptanceBadge acceptance={item.acceptance} />
                      </td>
                      <td className={master.wrapCell}>
                        {item.assignments.length === 0
                          ? k('none')
                          : item.assignments.map((a) => assignmentLabel(a, groupName)).join(', ')}
                      </td>
                      <td>{item.reviewDay ? formatDay(item.reviewDay) : k('none')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </>
  );
}

type Dialog =
  | { kind: Exclude<RaEventKind, 'accepted'> }
  | { kind: 'accepted'; acceptor: RaAcceptor }
  | { kind: 'publish' }
  | { kind: 'revise' }
  | null;

function OfficeDetail({
  id,
  list,
  office,
  onBack,
  onNotice,
}: {
  id: string;
  list: RaLibraryList;
  office: NonNullable<RaLibrarySource['office']>;
  source: RaLibrarySource;
  onBack: () => void;
  onNotice: (notice: Notice | null) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.${key}`, options);
  const message = useLibraryError();
  const [detail, setDetail] = useState<RaLibraryDetail | null>(null);
  const [draft, setDraft] = useState<RaLibraryContent | null>(null);
  const [tab, setTab] = useState<BuilderTab>('general');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const show = (next: RaLibraryDetail) => {
    setDetail(next);
    setDraft(next.content);
  };
  useEffect(() => {
    office.detail(id).then(
      (next) => {
        setDetail(next);
        setDraft(next.content);
      },
      (error) => setLoadError(errorCode(error)),
    );
  }, [office, id]);
  const dirty = useMemo(
    () => (detail && draft ? JSON.stringify(detail.content) !== JSON.stringify(draft) : false),
    [detail, draft],
  );
  if (loadError) {
    return (
      <Panel>
        <LoadError title={k('empty.error')} code={loadError} onRetry={onBack} />
      </Panel>
    );
  }
  if (!detail || !draft) {
    return (
      <Panel flush aria-label={k('title')}>
        <TableSkeleton columns={6} label={k('title')} />
      </Panel>
    );
  }
  const matrix = list.matrix?.definition ?? LEGACY_RISK_MATRIX;
  const can = detail.can;
  const run = async (fn: () => Promise<RaLibraryDetail>, done: string): Promise<string | null> => {
    setBusy(true);
    try {
      show(await fn());
      onNotice({ tone: 'success', text: done });
      return null;
    } catch (error) {
      return errorCode(error);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    const code = await run(
      () => office.save(detail.id, detail.rowVersion, draft),
      k('actions.saved'),
    );
    if (code) onNotice({ tone: 'danger', text: message(code) });
  };
  const event = (
    kind: RaEventKind,
    input: { reason: string; password: string },
    acceptor?: RaAcceptor,
  ) =>
    run(
      () =>
        office.event(detail.id, {
          kind,
          contentHash: detail.contentHash,
          ...(input.reason ? { reason: input.reason } : {}),
          ...(acceptor ? { acceptor } : {}),
          ...(input.password ? { password: input.password } : {}),
        }),
      k(`actions.done.${kind}`),
    );
  const role = (r: string) => t(`risk.matrixProfile.acceptors.${r}`);
  const nextRevision = (() => {
    const [major, minor] = detail.revision.split('.').map(Number);
    return `${major}.${(minor ?? 0) + 1}`;
  })();
  const dialogProps = {
    onClose: () => setDialog(null),
    reauthKind: 'ra_library' as const,
    messageOf: (code: string) => message(code),
  };
  return (
    <Panel flush aria-label={`${detail.code} · ${detail.title}`}>
      <div className={classes.head}>
        <div className={classes.headTitle}>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => {
              if (dirty && !window.confirm(t('common.unsaved.message'))) return;
              onBack();
            }}
            style={{ alignSelf: 'flex-start' }}
          >
            {k('actions.back')}
          </Button>
          <h2>
            {detail.code} · {draft.general.title}
          </h2>
          <span className={classes.meta}>
            <RaStatusBadge status={detail.status} />
            <span>{k('header.revision', { revision: detail.revision })}</span>
            <span>
              {detail.publishedRevision
                ? k('header.published', { revision: detail.publishedRevision })
                : k('header.noPublished')}
            </span>
            {detail.activityCode ? (
              <span>{k('header.activity', { code: detail.activityCode })}</span>
            ) : null}
            <AcceptanceBadge acceptance={detail.acceptance} />
            {!can.edit ? <StatusBadge tone="neutral">{k('header.readOnly')}</StatusBadge> : null}
          </span>
        </div>
        <div className={classes.actions}>
          {can.edit && dirty ? (
            <>
              <Button variant="default" onClick={() => setDraft(detail.content)}>
                {k('actions.discardChanges')}
              </Button>
              <Button loading={busy} onClick={() => void save()}>
                {k('actions.save')}
              </Button>
            </>
          ) : null}
          {can.submit && !dirty ? (
            <Button
              leftSection={<IconSend size={16} />}
              onClick={() => setDialog({ kind: 'submitted' })}
            >
              {k('actions.submit')}
            </Button>
          ) : null}
          {can.startReview ? (
            <Button onClick={() => setDialog({ kind: 'review_started' })}>
              {k('actions.startReview')}
            </Button>
          ) : null}
          {can.accept.map((acceptor) => (
            <Button
              key={acceptor}
              variant="light"
              onClick={() => setDialog({ kind: 'accepted', acceptor })}
            >
              {k('actions.accept', { role: role(acceptor) })}
            </Button>
          ))}
          {can.return ? (
            <Button variant="default" color="red" onClick={() => setDialog({ kind: 'returned' })}>
              {k('actions.return')}
            </Button>
          ) : null}
          {can.approve ? (
            <Button
              leftSection={<IconCircleCheck size={16} />}
              onClick={() => setDialog({ kind: 'approved' })}
            >
              {k('actions.approve')}
            </Button>
          ) : null}
          {can.publish ? (
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => setDialog({ kind: 'publish' })}
            >
              {k('actions.publish', { revision: detail.revision })}
            </Button>
          ) : null}
          {can.revise ? (
            <Button
              variant="default"
              leftSection={<IconEdit size={16} />}
              onClick={() => setDialog({ kind: 'revise' })}
            >
              {k('actions.revise')}
            </Button>
          ) : null}
        </div>
      </div>
      {/* İade bandı (ADR 0116 Ek): iade eden, zaman, gerekçe; ofiste UTC */}
      {detail.status === 'returned' ? (
        <ReturnedBanner notice={detail.returnNotice} hint={can.edit} />
      ) : null}
      <RaBuilder
        content={draft}
        matrix={matrix}
        editable={can.edit}
        onChange={setDraft}
        hazards={list.hazards}
        code={detail.code}
        revision={detail.revision}
        tab={tab}
        onTab={(next) => setTab(BUILDER_TABS.includes(next) ? next : 'general')}
        issues={detail.issues}
        publication={
          <RaPublication
            detail={detail}
            vessels={list.vessels}
            busy={busy}
            onAssign={(target) =>
              void run(() => office.assign(detail.id, target), k('publication.assign')).then(
                (code) => code && onNotice({ tone: 'danger', text: message(code) }),
              )
            }
            onUnassign={(assignmentId, rowVersion) =>
              void run(
                () => office.unassign(detail.id, assignmentId, rowVersion),
                k('publication.assignments'),
              ).then((code) => code && onNotice({ tone: 'danger', text: message(code) }))
            }
          />
        }
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'submitted'}
        title={k('dialog.submitTitle')}
        body={k('dialog.submitBody', { revision: detail.revision })}
        reason="optional"
        password={false}
        confirmLabel={k('actions.submit')}
        onConfirm={(input) => event('submitted', input)}
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'review_started'}
        title={k('dialog.reviewTitle')}
        body={k('dialog.reviewBody', { revision: detail.revision })}
        reason="none"
        password
        confirmLabel={k('actions.startReview')}
        onConfirm={(input) => event('review_started', input)}
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'returned'}
        title={k('dialog.returnTitle')}
        body={k('dialog.returnBody')}
        reason="required"
        password
        danger
        confirmLabel={k('actions.return')}
        onConfirm={(input) => event('returned', input)}
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'accepted'}
        title={k('dialog.acceptTitle')}
        body={k('dialog.acceptBody', {
          revision: detail.revision,
          role: dialog?.kind === 'accepted' ? role(dialog.acceptor) : '',
        })}
        reason="optional"
        password
        confirmLabel={k('dialog.confirm')}
        onConfirm={(input) =>
          dialog?.kind === 'accepted'
            ? event('accepted', input, dialog.acceptor)
            : Promise.resolve('INVALID_TRANSITION')
        }
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'approved'}
        title={k('dialog.approveTitle', { revision: detail.revision })}
        body={k('dialog.approveBody', { revision: detail.revision })}
        note={can.selfApproval ? k('dialog.approveSelf') : null}
        reason="optional"
        password
        confirmLabel={k('actions.approve')}
        onConfirm={(input) => event('approved', input)}
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'publish'}
        title={k('dialog.publishTitle', { revision: detail.revision })}
        body={k('dialog.publishBody')}
        reason="none"
        password
        confirmLabel={k('actions.publish', { revision: detail.revision })}
        onConfirm={(input) =>
          run(
            () => office.publish(detail.id, detail.contentHash, input.password || undefined),
            k('actions.done.published', { revision: detail.revision }),
          )
        }
      />
      <ActionDialog
        {...dialogProps}
        opened={dialog?.kind === 'revise'}
        title={k('dialog.reviseTitle')}
        body={k('dialog.reviseBody', {
          next: nextRevision,
          published: detail.publishedRevision ?? detail.revision,
        })}
        reason="none"
        password={false}
        confirmLabel={k('actions.revise')}
        onConfirm={() =>
          run(
            () => office.revise(detail.id, detail.rowVersion),
            k('actions.done.revised', {
              revision: nextRevision,
              published: detail.publishedRevision ?? detail.revision,
            }),
          )
        }
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// Gemi

function ShipLibrary({ source }: { source: RaLibrarySource }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.${key}`, options);
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | { kind: 'ready'; library: RaVesselLibrary }
  >({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState<RaVersionView | null>(null);
  const [tab, setTab] = useState<BuilderTab>('general');
  const load = useCallback(async () => {
    try {
      setState({
        kind: 'ready',
        library: await (source.vessel as NonNullable<typeof source.vessel>)(),
      });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  const library = state.kind === 'ready' ? state.library : null;
  const items = useMemo(
    () => (library ? filterVesselLibrary(library.versions, query) : []),
    [library, query],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const availability = (v: RaVesselLibrary['versions'][number]) => (
    <StatusBadge tone={v.current ? 'success' : 'neutral'}>
      {k(`availability.${v.current ? 'current' : (v.reason ?? 'not_assigned')}`)}
    </StatusBadge>
  );
  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('shipTitle')}
        description={k('shipDescription')}
        icon={<IconBooks size={26} />}
      />
      <p className={master.note}>{k('shipNote')}</p>
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('empty.error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !library ? (
        <Panel flush aria-label={k('shipTitle')}>
          <TableSkeleton columns={6} label={k('shipTitle')} />
        </Panel>
      ) : version ? (
        <Panel flush aria-label={`${version.code} · ${version.title}`}>
          <div className={classes.head}>
            <div className={classes.headTitle}>
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconArrowLeft size={14} />}
                onClick={() => setVersion(null)}
                style={{ alignSelf: 'flex-start' }}
              >
                {k('actions.back')}
              </Button>
              <h2>
                {version.code} · {version.title}
              </h2>
              <span className={classes.meta}>
                <span>{k('header.readOnlyVersion', { revision: version.revision })}</span>
                {(() => {
                  const listed = library.versions.find((v) => v.id === version.id);
                  return listed ? availability(listed) : null;
                })()}
              </span>
            </div>
          </div>
          <RaBuilder
            content={version.content}
            matrix={version.matrixDefinition}
            editable={false}
            code={version.code}
            revision={version.revision}
            tab={tab}
            onTab={setTab}
            publication={
              <section className={classes.card} aria-label={k('publication.versions')}>
                <div className={classes.cardHead}>
                  <span>{k('publication.versions')}</span>
                </div>
                <div className={classes.cardBody}>
                  <VersionList versions={[version]} />
                </div>
              </section>
            }
          />
        </Panel>
      ) : (
        <>
          <div className={master.stats}>
            <StatTile
              icon={<IconShieldCheck size={22} />}
              label={k('stats.current')}
              value={library.versions.filter((v) => v.current).length}
              variant="plain"
            />
            <StatTile
              icon={<IconBooks size={22} />}
              label={k('stats.published')}
              value={library.versions.length}
              variant="plain"
            />
          </div>
          <Panel flush aria-label={k('shipTitle')}>
            <div className={master.toolbar}>
              <TextInput
                className={master.search}
                leftSection={<IconSearch size={16} aria-hidden />}
                placeholder={k('search')}
                aria-label={k('search')}
                value={query}
                onChange={(event) => {
                  setQuery(event.currentTarget.value);
                  setPage(1);
                }}
              />
            </div>
            {library.versions.length === 0 ? (
              <EmptyState title={k('empty.title')} description={k('empty.ship')} />
            ) : items.length === 0 ? (
              <NoMatch title={k('empty.noMatch')} onClear={() => setQuery('')} />
            ) : (
              <>
                <section
                  className={table.tableWrap}
                  aria-label={k('tableLabel')}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                  tabIndex={0}
                >
                  <table className={`${table.table} ${purchasing.table}`}>
                    <thead>
                      <tr>
                        <th scope="col">{k('columns.code')}</th>
                        <th scope="col">{k('columns.title')}</th>
                        <th scope="col">{k('columns.revision')}</th>
                        <th scope="col">{k('columns.availability')}</th>
                        <th scope="col" data-numeric>
                          {k('columns.rows')}
                        </th>
                        <th scope="col">{k('columns.inherent')}</th>
                        <th scope="col">{k('columns.residual')}</th>
                        <th scope="col">{k('columns.review')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((v) => (
                        <tr key={v.id}>
                          <td>
                            <button
                              type="button"
                              className={master.codeButton}
                              aria-label={k('open', { code: v.code })}
                              onClick={async () => {
                                setTab('general');
                                setVersion(await source.version(v.id));
                              }}
                            >
                              {v.code}
                            </button>
                            {v.migrated ? (
                              <span className={master.sub}>{k('migrated')}</span>
                            ) : null}
                          </td>
                          <td className={master.wrapCell}>{v.title}</td>
                          <td>{v.revision}</td>
                          <td>{availability(v)}</td>
                          <td data-numeric>{v.rows}</td>
                          <td>
                            {v.inherent ? (
                              <LevelBadge level={v.inherent.level} score={v.inherent.score} />
                            ) : (
                              k('none')
                            )}
                          </td>
                          <td>
                            {v.residual ? (
                              <LevelBadge level={v.residual.level} score={v.residual.score} />
                            ) : (
                              k('none')
                            )}
                          </td>
                          <td>{v.reviewDay ? formatDay(v.reviewDay) : k('none')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
                <TablePager
                  page={current}
                  pages={pages}
                  summary={k('showing', {
                    from: (current - 1) * PAGE_SIZE + 1,
                    to: Math.min(current * PAGE_SIZE, items.length),
                    total: items.length,
                  })}
                  label={k('pagination')}
                  onPage={setPage}
                />
              </>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
